import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../../utils/tokenManager";
import axios from "axios";
import moment from "moment";
import "moment-timezone";
import { useContactContext } from "../../../contexts/ContactContext";

/**
 * Extracted from NoteInfo.jsx (desktop) - same validation rules, payload
 * shape, and endpoints, byte-for-byte. NoteInfo.jsx now calls this hook
 * too instead of keeping its own copy, so both desktop and mobile always
 * behave identically; a rule change here applies to both automatically.
 *
 * `callType` replaces desktop's `activeButton` (which NoteInfo derived
 * from the URL) - the caller decides how to determine it, this hook
 * only needs the resulting string ('cold-calls' | 'follow-up' |
 * 'meeting' | 'meeting-recovery').
 */
export function useCallOutcomeSubmit({ contact, callType, onNextContact }) {
  const { refreshContacts } = useContactContext();
  const { t } = useTranslation();

  const [channel, setChannelState] = useState({ channel: "" });
  const [nextCallModal, setNextCallModal] = useState(false);
  const [answered, setAnswered] = useState("");
  const [confirmed, setConfirmed] = useState("");
  const [confirmedRecovery, setConfirmedRecovery] = useState("");
  const [scheduled, setScheduled] = useState("");
  const [interested, setInterested] = useState("");
  const [selectedDate, setSelectedDate] = useState(moment());
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [noteError, setNoteError] = useState("");
  const [channelError, setChannelError] = useState("");
  const [errors, setErrors] = useState({});
  const [errorModal, setErrorModal] = useState(false);

  const contactId = contact?.contactId;
  const meetingDate = contact?.date;

  const channels = [
    { value: "Email", label: t("email") },
    { value: "WhatsApp", label: t("whatsapp") },
    { value: "Call", label: t("call") },
  ];

  const isColdOrFollowUp = callType === "cold-calls" || callType === "follow-up";

  const handleChannel = (field, value) => {
    setChannelState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetFields = () => {
    setAnswered("");
    setConfirmed("");
    setConfirmedRecovery("");
    setScheduled("");
    setInterested("");
    setSelectedDate(moment());
    setNotes("");
    setChannelState({ channel: "" });
    setError("");
    setNoteError("");
    setErrors({});
  };

  useEffect(() => {
    resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  useEffect(() => {
    setInterested("");
    setScheduled("");
    setChannelState({ channel: "" });
    setSelectedDate(moment());
  }, [answered]);

  useEffect(() => {
    setScheduled("");
    setSelectedDate(moment());
  }, [interested]);

  useEffect(() => {
    if (
      confirmedRecovery ||
      confirmed ||
      answered ||
      interested ||
      scheduled ||
      channel
    ) {
      setError("");
      setChannelError("");
    }
  }, [confirmedRecovery, confirmed, answered, interested, scheduled, channel]);

  const validateForm = () => {
    const notesRequired = !(isColdOrFollowUp && answered === "no");

    if (notesRequired && !notes.trim()) {
      setNoteError(t("validation.required"));
      return false;
    }

    if (callType === "meeting-recovery") {
      if (!confirmedRecovery) {
        setError(t("validation.requiredCheck"));
        return false;
      }
      if (confirmedRecovery === "yes") {
        if (!selectedDate) {
          setError(t("validation.requiredCheck"));
          return false;
        }
        if (moment(selectedDate).isBefore(moment(), "minute")) {
          setError(t("validation.dateCannotBePast"));
          return false;
        }
      }
      return true;
    }

    if (callType === "meeting") {
      if (!confirmed) {
        setError(t("validation.requiredCheck"));
        return false;
      }
      if (confirmed === "reschedule") {
        if (!selectedDate) {
          setError(t("validation.requiredCheck"));
          return false;
        }
        if (moment(selectedDate).isBefore(moment(), "minute")) {
          setError(t("validation.dateCannotBePast"));
          return false;
        }
      }
      return true;
    }

    if (callType === "follow-up" || callType === "cold-calls") {
      if (!answered) {
        setError(t("validation.requiredCheck"));
        return false;
      }

      if (answered === "callLater") {
        if (!selectedDate) {
          setError(t("validation.requiredCheck"));
          return false;
        }
        if (moment(selectedDate).isBefore(moment(), "minute")) {
          setError(t("validation.dateCannotBePast"));
          return false;
        }
      }

      if (answered === "yes") {
        if (!interested) {
          setError(t("validation.requiredCheck"));
          return false;
        }

        if (interested === "yes") {
          if (!scheduled) {
            setError(t("validation.requiredCheck"));
            return false;
          }
          if (scheduled === "yes") {
            if (!selectedDate) {
              setError(t("validation.requiredCheck"));
              return false;
            }
            if (moment(selectedDate).isBefore(moment(), "minute")) {
              setError(t("validation.dateCannotBePast"));
              return false;
            }
          }
        }
      }

      if (
        callType === "cold-calls" &&
        answered === "yes" &&
        !channel.channel
      ) {
        setChannelError(t("validation.requiredChannel"));
        return false;
      }
    }

    return true;
  };

  const addWorkingDays = (startDate, numberOfDays) => {
    let result = moment(startDate);
    let addedDays = 0;
    while (addedDays < numberOfDays) {
      result = result.add(1, "day");
      const day = result.day();
      if (day !== 0 && day !== 6) {
        addedDays += 1;
      }
    }
    return result;
  };

  const handleNextCall = () => {
    if (!validateForm()) {
      return;
    }
    setNextCallModal(true);
  };

  const submitAndGoNext = async () => {
    if (!contactId) {
      setError(t("validation.contactIdMissing"));
      return;
    }

    setIsSubmitting(true);
    setError("");

    const controller = new AbortController();

    const effectiveNotes =
      isColdOrFollowUp && answered === "no" && !notes.trim()
        ? t("agent.noteInfo.notAnsweredDefault", "No contesto")
        : notes;

    try {
      const accessToken = await getValidToken();

      if (
        confirmed === "yes" ||
        confirmed === "no" ||
        confirmed === "reschedule" ||
        scheduled === "yes" ||
        (interested === "yes" && scheduled === "yes") ||
        (callType === "meeting-recovery" &&
          (confirmedRecovery === "yes" || confirmedRecovery === "no"))
      ) {
        const meetingData = {
          Confirmation_Date:
            confirmed === "yes" ? new Date().toISOString() : null,
          Confirmed: confirmed === "yes",
          Date:
            callType === "meeting-recovery"
              ? confirmedRecovery === "yes"
                ? selectedDate.toISOString()
                : meetingDate || null
              : confirmed === "reschedule" || scheduled === "yes"
              ? selectedDate.toISOString()
              : meetingDate || null,
          Recovering: confirmedRecovery === "yes" || confirmed === "reschedule",
          Result: effectiveNotes,
        };

        if (confirmed === "yes") {
          meetingData.Attend = true;
        } else if (confirmed === "no" || confirmedRecovery === "no") {
          meetingData.Attend = false;
        }

        const createdLogRes = await axios.post(
          `${process.env.REACT_APP_APIBASEURL}/api/meetings/${contactId}/log`,
          meetingData,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
          }
        );

        if (callType === "meeting-recovery") {
          const meetingLogId = createdLogRes?.data?.meetingLogId;
          if (meetingLogId) {
            const recoveryUpdatePayload = {
              Confirmed: false,
              Recovering: true,
              Result: effectiveNotes,
            };
            if (confirmedRecovery === "no") {
              recoveryUpdatePayload.Attend = false;
            }
            await axios.put(
              `${process.env.REACT_APP_APIBASEURL}/api/meetings/log/${meetingLogId}`,
              recoveryUpdatePayload,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
                signal: controller.signal,
              }
            );
          }
        }
      }
      if (
        answered === "yes" ||
        answered === "no" ||
        answered === "callLater" ||
        scheduled === "yes" ||
        (interested === "yes" && scheduled === "yes")
      ) {
        const callData = {
          Accept_Meeting: scheduled === "yes",
          Answered: answered === "yes" || answered === "callLater",
          ContactId: contactId,
          FollowUp_Date:
            answered === "callLater"
              ? selectedDate.toISOString()
              : answered === "yes" && interested === "yes" && scheduled !== "yes"
              ? addWorkingDays(moment(), 1).toISOString()
              : null,
          InterestLevel:
            interested === ""
              ? null
              : interested === "yes" || interested === "sendAgain"
              ? 1
              : 0,
          IsInterested:
            interested === ""
              ? null
              : interested === "yes" || interested === "sendAgain"
              ? true
              : false,
          Meeting_Date: scheduled === "yes" ? selectedDate.toISOString() : null,
          Notes: callType === "meeting" ? null : effectiveNotes,
          PreferredChannel: channel.channel || contact.channel || null,
          Resend_Info:
            interested === ""
              ? null
              : interested === "sendAgain" || interested === "yes",
          Result: callType === "meeting" ? null : effectiveNotes,
        };

        await axios.post(
          `${process.env.REACT_APP_APIBASEURL}/api/calls/${contactId}/log`,
          callData,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
          }
        );
      }

      await refreshContacts();
      resetFields();
      setIsSubmitting(false);
      setNextCallModal(false);
      onNextContact?.();
    } catch (error) {
      const errorMessage = error.response?.data?.error || t("error.generic");
      setErrorModal(true);
      setErrors({ submit: errorMessage });
      setIsSubmitting(false);
    }
  };

  const handleModal = () => {
    setErrorModal(false);
  };

  return {
    channel,
    handleChannel,
    channels,
    nextCallModal,
    setNextCallModal,
    answered,
    setAnswered,
    confirmed,
    setConfirmed,
    confirmedRecovery,
    setConfirmedRecovery,
    scheduled,
    setScheduled,
    interested,
    setInterested,
    selectedDate,
    setSelectedDate,
    notes,
    setNotes,
    isSubmitting,
    error,
    setError,
    noteError,
    setNoteError,
    channelError,
    errors,
    errorModal,
    isColdOrFollowUp,
    handleNextCall,
    submitAndGoNext,
    handleModal,
  };
}